import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

/* ตะกร้าห้องพัก — เก็บวันที่ + จำนวนคน + รายการห้องที่เลือก

   ทำไมต้องเป็น context ไม่ใช่ useState ใน Rooms.jsx:
   /rooms → /roomdetail/:id → กด Back ของเบราว์เซอร์ จะ remount Rooms แล้วตะกร้าหาย
   การส่งผ่าน navigate(state) แก้ได้แค่ขาไป ไม่แก้ปุ่ม Back (Back คืน state เก่าของ history entry นั้น)
   provider ที่อยู่เหนือ <Routes> รอดทุกการ navigate ภายในแอป และหายตอน hard reload — ตามที่ต้องการ
   checkIn/nights/adults ต้องอยู่ในนี้ด้วย เพราะตะกร้าที่ตรวจห้องว่างไว้สำหรับวันหนึ่ง
   ไม่มีความหมายเลยถ้าวันที่ reset กลับเป็นวันนี้ตอนกด Back

   ไม่ persist ลง storage โดยตั้งใจ: ห้องว่างขยับตลอด ตะกร้าที่ restore มาต้องโดน trim อยู่ดี
   แต่จะเด้ง toast ตอนเปิดหน้าโดยผู้ใช้ไม่ได้ทำอะไร — สับสนกว่าช่วย */

/* เพดานห้องต่อการจอง 1 ครั้ง — ตรงกับ MAX_GROUP_ROOMS ฝั่ง backend */
export const MAX_GROUP_ROOMS = 10;

const BookingCartContext = createContext(null);

const todayStr = () => new Date().toISOString().split("T")[0];

export function BookingCartProvider({ children }) {
  const [checkIn, setCheckIn] = useState(todayStr);
  const [nights,  setNights]  = useState(1);
  const [adults,  setAdults]  = useState(2);

  /* [{ roomTypeId, qty }] — 1 entry ต่อ 1 ประเภทเสมอ (บังคับใน setQty ไม่ใช่ที่ caller)
     เป็น array ไม่ใช่ map เพื่อให้ลำดับที่ใส่คือลำดับที่แสดงในแผงตะกร้า */
  const [items, setItems] = useState([]);

  /* reconcile ต้องรู้ผลทันทีว่าตัดอะไรไปบ้าง (เพื่อเด้ง toast) แต่ updater ของ setItems
     ถูกเรียกตอน re-render ไม่ใช่ตอนเรียก — อ่านค่าปัจจุบันผ่าน ref แทน
     และทำให้ reconcile มี identity คงที่ ไม่ไปเตะ effect ที่ fetch ห้องว่างซ้ำ */
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const totalRooms = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  const qtyOf = useCallback(
    (roomTypeId) => items.find(i => i.roomTypeId === roomTypeId)?.qty || 0,
    [items],
  );

  /* free = ห้องว่างจริงของประเภทนี้ (undefined = ยังไม่รู้ ไม่ต้อง clamp ด้วยค่านี้)
     เพดานรวมทั้งตะกร้าคือ MAX_GROUP_ROOMS — ไม่ใช่ต่อประเภท */
  const setQty = useCallback((roomTypeId, next, free) => {
    setItems(prev => {
      const cur    = prev.find(i => i.roomTypeId === roomTypeId)?.qty || 0;
      const others = prev.reduce((s, i) => s + (i.roomTypeId === roomTypeId ? 0 : i.qty), 0);
      const cap    = Math.min(
        Number.isFinite(free) ? free : Infinity,
        MAX_GROUP_ROOMS - others,
      );
      const qty = Math.max(0, Math.min(Math.floor(Number(next) || 0), cap));

      if (qty === cur) return prev;
      if (qty <= 0)    return prev.filter(i => i.roomTypeId !== roomTypeId);
      if (cur === 0)   return [...prev, { roomTypeId, qty }];
      return prev.map(i => (i.roomTypeId === roomTypeId ? { ...i, qty } : i));
    });
  }, []);

  const addOne     = useCallback((id, free) => setQty(id, qtyOf(id) + 1, free), [setQty, qtyOf]);
  const removeItem = useCallback((id) => setItems(prev => prev.filter(i => i.roomTypeId !== id)), []);
  const clear      = useCallback(() => setItems([]), []);

  /* วันที่เปลี่ยน → ห้องว่างเปลี่ยน → หั่นจำนวนที่เกินลงมา (ไม่ล้างทั้งตะกร้า
     เพราะ nights เป็น stepper ±1 คลิกพลาดทีเดียวไม่ควรทำให้เลือกใหม่หมด)
     คืน true ถ้ามีอะไรถูกลด เพื่อให้หน้าเว็บเด้ง toast บอก */
  const reconcile = useCallback((freeMap) => {
    let changed = false;
    const next = itemsRef.current
      .map(i => {
        const free = freeMap?.[i.roomTypeId]?.free;
        if (!Number.isFinite(free) || i.qty <= free) return i;
        changed = true;
        return { ...i, qty: free };
      })
      .filter(i => i.qty > 0);

    if (changed) {
      itemsRef.current = next;
      setItems(next);
    }
    return changed;
  }, []);

  const value = useMemo(() => ({
    checkIn, setCheckIn, nights, setNights, adults, setAdults,
    items, totalRooms, qtyOf, setQty, addOne, removeItem, clear, reconcile,
  }), [checkIn, nights, adults, items, totalRooms, qtyOf, setQty, addOne, removeItem, clear, reconcile]);

  return (
    <BookingCartContext.Provider value={value}>
      {children}
    </BookingCartContext.Provider>
  );
}

export function useBookingCart() {
  return useContext(BookingCartContext);
}
